
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import Icon from "../../components/AppIcon";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import SearchInput from "./components/SearchInput";
import Pagination from "./components/Pagination";
import Toast from "./components/Toast";
import DataTable from "./components/DataTable";
import EmailSendModal from "./components/EmailSendModal";
import ViewContentModal from "./components/ViewContentModal";
import DeleteModal from "pages/inspiration/components/DeleteModal";
import EmailDetailModal from "./components/EmailDetailModal";

const apiUrl = import.meta.env.VITE_API_URL;

const EmailMarketing = () => {
  const defaultSort = { key: "createdAt", direction: "desc" };
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState(defaultSort);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const [emailSendModalOpen, setEmailSendModalOpen] = useState(false);
  const [viewContentModalOpen, setViewContentModalOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blog, setBlog] = useState(null);
  const [submitLoader, setSubmitLoader] = useState(false);

  // NEW STATE VARIABLES
  const [selectedItems, setSelectedItems] = useState([]);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedEmailDetail, setSelectedEmailDetail] = useState(null);

  const searchTriggered = useRef(false);
  const searchClicked = useRef(false);
  const debounced = useRef(null);

// console.log(data,"-----------------data-------------------")
//   console.log(selectedEmailDetail,"-----------------selectedEmailDetail-------------------")

  // UPDATED COLUMNS
  const columns = [
    { key: "select", label: "", sortable: false },
    { key: "sno", label: "Sno", sortable: false },
    { key: "to", label: "To", sortable: true },
    { key: "description", label: "Description", sortable: false },
    { key: "sentAt", label: "Sent At", sortable: false },
    { key: "status", label: "Status", sortable: false },
    { key: "actions", label: "Actions", sortable: false },
  ];

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        search: searchQuery,
        page: currentPage,
        limit: itemsPerPage,
      });
      const res = await fetch(`${apiUrl}/api/admin-user/email-marketing/list?${params.toString()}`);
      const json = await res.json();
      // console.log(json,"--------------response-----------------")
      if (res.ok) {
        setData(json.data);
        setTotalPages(json.totalPages);
      } else {
        throw new Error(json.error || "Failed to fetch data.");
      }
      setError(null);
    } catch (err) {
      setError("Failed to fetch data.");
      setToast({ show: true, message: "Failed to fetch data.", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!searchTriggered.current) fetchData();
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    if (searchTriggered.current) {
      searchTriggered.current = false;
      fetchData();
    }
  }, [searchTriggered.current]);

  useEffect(() => {
    if (searchClicked.current) {
      debounced.current = setTimeout(() => {
        setCurrentPage(1);
        setSortConfig(defaultSort);
        setItemsPerPage(10);
        searchTriggered.current = true;
      }, 1000);
    }
    return () => clearTimeout(debounced.current);
  }, [searchQuery]);

  const requestSort = useCallback(
    (key) => {
      let direction = "ascending";
      if (sortConfig.key === key && sortConfig.direction === "ascending") {
        direction = "descending";
      }
      setSortConfig({ key, direction });
    },
    [sortConfig]
  );

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleOpenBlogModal = (blog = null) => {
    setSelectedBlog(blog);
    setEmailSendModalOpen(true);
  };

  const handleOpenViewContentModal = (blog) => {
    setSelectedBlog(blog);
    setViewContentModalOpen(true);
  };


  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(data.map(item => item._id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) 
        ? prev.filter(itemId => itemId !== id)
        : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      setToast({ show: true, message: "Please select items to delete", type: "warning" });
      return;
    }
    
    try {
      const deletePromises = selectedItems.map(id => 
        fetch(`${apiUrl}/api/admin-user/email-marketing?id=${id}`, { method: "DELETE" })
      );
      
      await Promise.all(deletePromises);
      
      const remaining = data.filter(item => !selectedItems.includes(item._id));
      setData(remaining);
      setSelectedItems([]);
      setToast({ 
        show: true, 
        message: `Successfully deleted ${selectedItems.length} email(s)`, 
        type: "success" 
      });
    } catch (err) {
      setToast({ show: true, message: "Failed to delete emails", type: "error" });
    }
    setShowDeleteModal(false)
  };

  const handleViewDetail = (email) => {
    setSelectedEmailDetail(email);
    // console.log(email,"-----------------email-------------------")
    setDetailModalOpen(true);
  };

  const handleTableAction = (blog, action) => {
   if (action === "view") {
    handleViewDetail(blog);
    // console.log(blog,"-----------------blog-------------------")
    
  }
};

  return (
    <div className="overflow-x-hidden">
      <div className="flex bg-background">
        <Sidebar
          variant="with-categories"
          onToggle={(collapsed) => setIsCollapsed(collapsed)}
        />
        <div
          className={`w-full transition-all duration-300 ${isCollapsed ? "lg:pl-16" : "lg:pl-64"
            }`}
        >
          <Header isCollapsed={isCollapsed} variant="default" />
          <main className="overflow-y-auto p-4 md:p-6 bg-background">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Email Marketing</h1>
              </div>
              <div className="flex gap-3 mt-4 md:mt-0">
                {selectedItems.length > 0 && (
                  <button
                    onClick={() => {
                      setShowDeleteModal(true);
                    }}
                    className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 flex items-center gap-2"
                  >
                    <Icon name="Trash2" size={18} />
                    Delete Selected ({selectedItems.length})
                  </button>
                )}
                <button
                  onClick={() => handleOpenBlogModal()}
                  className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700"
                >
                  Send mail
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b w-full border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <SearchInput
                  onClick={() => (searchClicked.current = true)}
                  onBlur={() => (searchClicked.current = false)}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClear={() => {
                    setSearchQuery("");
                    searchClicked.current = true;
                  }}
                  placeholder="Search emails..."
                />
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">Show:</span>
                  <select
                    className="form-select rounded-md border-gray-300 shadow-sm text-sm"
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    aria-label="Items per page"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              <DataTable
                columns={columns}
                data={data}
                isLoading={isLoading}
                error={error}
                sortConfig={sortConfig}
                onSort={requestSort}
                onRetry={fetchData}
                emptyMessage="No emails found."
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onAction={handleTableAction}
                selectedItems={selectedItems}
                onSelectAll={handleSelectAll}
                onSelectItem={handleSelectItem}
              />

              {!isLoading && !error && data?.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  totalItems={data.length}
                  itemsPerPage={itemsPerPage}
                  showingFrom={(currentPage - 1) * itemsPerPage + 1}
                  showingTo={Math.min(currentPage * itemsPerPage, data.length)}
                />
              )}
            </div>
          </main>
        </div>
      </div>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      {emailSendModalOpen && (
        <EmailSendModal
          onClose={() => setEmailSendModalOpen(false)}
        />
      )}

      {viewContentModalOpen && selectedBlog && (
        <ViewContentModal
          blog={selectedBlog}
          onClose={() => setViewContentModalOpen(false)}
        />
      )}

      {detailModalOpen && selectedEmailDetail && (
        <EmailDetailModal
          email={selectedEmailDetail}
          onClose={() => setDetailModalOpen(false)}
        />
      )}

      {showDeleteModal &&
        <DeleteModal
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => handleBulkDelete()}
        />
      }
    </div>
  );
};

export default EmailMarketing;